import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, Loader2 } from "lucide-react";
import { api, formatINR } from "../lib/api";

export default function OrderConfirm() {
  const { orderNumber } = useParams();
  const [params] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null); // PAID | PENDING | FAILED | null
  const cameFromGateway = params.has("cf_order_id");

  const fetchOrder = useCallback(async () => {
    const r = await api.get(`/orders/${orderNumber}`);
    setOrder(r.data);
    return r.data;
  }, [orderNumber]);

  const pollStatus = useCallback(async () => {
    try {
      const r = await api.get(`/payments/cashfree/status/${orderNumber}`);
      setPaymentStatus(r.data.status);
      return r.data.status;
    } catch { return null; }
  }, [orderNumber]);

  useEffect(() => {
    fetchOrder();
    if (!cameFromGateway) return;
    let attempts = 0;
    const tick = async () => {
      const s = await pollStatus();
      attempts += 1;
      if (s !== "PAID" && s !== "FAILED" && attempts < 8) {
        setTimeout(tick, 2500);
      }
    };
    tick();
  }, [fetchOrder, pollStatus, cameFromGateway]);

  if (!order) return (
    <div className="min-h-[60vh] flex items-center justify-center text-white/60">
      <Loader2 className="animate-spin mr-2" size={16} /> Loading...
    </div>
  );

  // Derive UI state
  const isCOD = order.payment_method === "cod";
  const paid = paymentStatus === "PAID" || (isCOD && !cameFromGateway) || order.payment_status === "PAID";
  const failed = paymentStatus === "FAILED" || order.payment_status === "FAILED";
  const pending = cameFromGateway && !paid && !failed;

  return (
    <div data-testid="order-confirm-page" className="min-h-screen max-w-3xl mx-auto px-6 py-16 text-center">
      {paid && <CheckCircle2 size={56} className="text-[#D96A1B] mx-auto" data-testid="payment-status-paid" />}
      {failed && <XCircle size={56} className="text-red-500 mx-auto" data-testid="payment-status-failed" />}
      {pending && <Clock size={56} className="text-yellow-400 mx-auto animate-pulse" data-testid="payment-status-pending" />}

      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tighter mt-6">
        {paid && "Order Confirmed"}
        {failed && "Payment Failed"}
        {pending && "Verifying Payment"}
      </h1>
      <p className="text-white/65 font-body mt-3">
        {paid && `Welcome to the Forge family, ${order.customer_name.split(" ")[0]}.`}
        {failed && "Your payment didn't go through. No money was charged."}
        {pending && "Please wait while we confirm your payment with Cashfree…"}
      </p>
      <div className="overline mt-1">Order #{order.order_number}</div>

      <div className="mt-10 border border-white/10 p-6 text-left space-y-4">
        {order.items.map((i) => (
          <div key={i.product_id} className="flex gap-4">
            <img src={i.image} alt="" className="w-16 h-20 object-cover" />
            <div className="flex-1">
              <div className="font-display uppercase tracking-tight">{i.name}</div>
              <div className="text-xs text-white/50 font-body">Qty {i.quantity}</div>
            </div>
            <div className="font-display">{formatINR(i.price * i.quantity)}</div>
          </div>
        ))}
        <div className="border-t border-white/10 pt-4 flex justify-between font-display text-xl">
          <span>{paid ? "Total Paid" : "Order Total"}</span>
          <span data-testid="oc-total" className="text-[#D96A1B]">{formatINR(order.total)}</span>
        </div>
      </div>

      <p className="mt-6 text-sm text-white/60 font-body">Shipping to {order.address}, {order.city}, {order.state} - {order.pincode}</p>

      <div className="mt-8 flex flex-wrap gap-3 justify-center">
        {failed && (
          <Link to="/checkout" data-testid="retry-payment" className="btn-orange">Retry Payment</Link>
        )}
        <Link to={`/track-order?order=${order.order_number}`} data-testid="track-this-order" className="btn-ghost">Track This Order</Link>
        <Link to="/shop" className="btn-ghost">Keep Shopping</Link>
      </div>
    </div>
  );
}
